import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  setShowBankingServiceUnavailableError,
  useAppDispatch,
  useAppRouter,
  useCreateBankingSessionMutation,
  usePBIRejected,
  useShowBankingServiceUnavailableError,
} from '../../../state'
import { Route } from '../../../config/router'
import styles from './style.module.css'
import { BASE_PATH, app } from '../../../env'
import { Button, Text, useIsBreakpointLargeAndUp } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { Sticky } from '../../Sticky'
import clsx from 'clsx'
import { PayInFourCta } from '../../PayInFourCta'
import { TrackingEvent } from '../../../model/monitoring'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useFlag } from '@afterpaytouch/feature'
import { Features } from '../../../model'
import { useMonitoringWithEnduringEventProperties } from '../../../integrations/monitoring'
import { useRouter } from 'next/router'
import { ConsumerLendingErrorModal } from '../../ConsumerLendingErrorModal'

export const PayMonthlyButton: FunctionComponent = () => {
  const { t } = useTranslation()
  const PORTAL_URL = app.PORTAL_URL
  const [lock, setLock] = useState<boolean>(false)
  const isOpenBankingDisabled = useFlag(Features.CONSUMER_LENDING_AU_OPEN_BANKING_FLOW_DISABLED)

  const { push, locale } = useAppRouter()
  const { query } = useRouter()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isDesktop = useIsBreakpointLargeAndUp()
  const isPBIRejected = usePBIRejected()
  const dispatch = useAppDispatch()
  const showBankingServiceUnavailableError = useShowBankingServiceUnavailableError()

  const [createBankSession, { isLoading: isBankingSessionLoading, isError: isBankingSessionError }] = useCreateBankingSessionMutation({
    fixedCacheKey: 'create-banking-session-cache',
  })

  const handleClick = async (): Promise<void> => {
    setLock(true)
    logEvent(TrackingEvent.PRESSED_CL_MARKETING_PAGE_APPLY_NOW_BUTTON)

    // skip open banking (Basiq) flow in dev if LD flag is set
    if (isOpenBankingDisabled) {
      return push(Route.INITIATE_BANK_VERIFICATION)
    }
    try {
      const result = await createBankSession({
        onCompleteUrl: `${PORTAL_URL}${BASE_PATH}/${locale}/initiate-bank-verification`,
        onCancelUrl: `${PORTAL_URL}${BASE_PATH}/${locale}/pay-monthly`,
      }).unwrap()

      if (result?.redirectUrl) {
        if (result?.hasAccounts || result?.hasFinances) {
          return push(Route.INITIATE_BANK_VERIFICATION)
        }
        // Don't log the browser closed event when redirecting to Basiq
        window.removeEventListener('beforeunload', (event) => {
          logEvent(TrackingEvent.PRESSED_CLOSE_BROWSER_TAB)
          event.preventDefault()
          event.stopPropagation()
          delete event.returnValue
        })
        window.open(result.redirectUrl, '_self')
      }
    } catch (e) {
      setLock(false)
      capture.error(e)
    }
  }

  useEffect(() => {
    if (query?.errorMsg === 'service-unavailable' && typeof isPBIRejected !== 'undefined') {
      if (isPBIRejected) {
        push(Route.TERMINAL_ERROR)
      } else {
        dispatch(setShowBankingServiceUnavailableError(true))
      }
    }
  }, [query, isPBIRejected])

  return (
    <>
      <Sticky mode='bottom'>
        {(isFixed: boolean) => (
          <div
            className={clsx(styles.stickyContainer, isDesktop ? styles.stickyContainerDesktop : styles.stickyContainerMobile, {
              [styles.stickyContainerFixed]: isFixed,
            })}
          >
            <div className={styles.buttonsContainerText}>
              <Text size='S' color='Gray50'>
                {t('consumerLending:payMonthly:closing')}
              </Text>
            </div>

            <Button
              kind='Secondary'
              padding='Fluid'
              onClick={handleClick}
              testNameSpace='pay-monthly'
              disabled={lock || isBankingSessionLoading}
              loading={lock}
            >
              {t('consumerLending:payMonthly:applyNow')}
            </Button>
            <PayInFourCta trackingEvent={TrackingEvent.PRESSED_CL_MARKETING_PAGE_PAY_IN_4_BUTTON} />
          </div>
        )}
      </Sticky>
      <ConsumerLendingErrorModal isError={isBankingSessionError || showBankingServiceUnavailableError} />
    </>
  )
}
